import React, { FC, createContext, useContext, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { Transition } from 'react-transition-group';
import { useMedia } from 'react-media-match';
import { useScrollPosition } from '@n8tb1t/use-scroll-position';
import useVisibility from '@empiriecom/module-components/hooks/useVisibility';
import { Product } from '@/src/interfaces';
import ProductInformation from './ProductInformation';
import StickyButtons from './StickyButtons';

const animationStyles = {
  entered: css`
    transform: translateY(0);
  `,
  entering: css`
    transform: translateY(-150%);
  `,
  exited: css`
    transform: translateY(-150%);
  `,
  exiting: css`
    transform: translateY(0);
  `,
};

type AreaWrapperProps = {
  animationState: string;
  position: 'top' | 'bottom';
  hasAnimation: boolean;
};

const AreaWrapper = styled.div<AreaWrapperProps>`
  padding: 0.5rem 0.625rem;
  position: fixed;
  left: 0;
  right: 0;
  z-index: 1001;
  background-color: #fff;
  box-shadow: 0 0 2px 0.5px rgba(0, 0, 0, 0.4);
  ${({ hasAnimation }) => hasAnimation && 'transition: all 400ms ease-in-out;'}
  ${({ position }) => (position === 'top' ? 'top: 0; transform: translateY(-150%);' : 'bottom: 0;')}
  ${({ animationState, position }) => position === 'top' && animationStyles[animationState]}
`;

const AreaContent = styled.div<{ isMobile: boolean }>`
  max-width: 1140px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  ${({ isMobile }) => isMobile && 'flex-direction: column; justify-content: flex-start;'}
`;

type StickyAreaProps = {
  /**
   * determines if sticky content is displayed
   */
  isContentVisible: boolean;
  /** product */
  product: Product;
  /**
   * pageYOffset for hiding StickyContent in page header area
   * @default -1
   */
  visibilityOffset?: number;
  /** function to force dimension selection */
  setHighlightUnselectedDimension?: (setHighlight: boolean) => void;
};

type StickyContextType = {
  isVisible: boolean;
  setIsVisible?: (isVisible: boolean) => void;
};

export const StickyContext = createContext<StickyContextType>({
  isVisible: false,
});

export const VisibilityWrapper: FC<{ className?: string }> = ({ children, className }) => {
  const isMobile = useMedia({ desktop: false, mobile: true, tablet: false });
  const [visibilityElementRef, isVisible] = useVisibility(true, isMobile);
  const { setIsVisible } = useContext(StickyContext);

  useEffect(() => {
    if (typeof setIsVisible === 'function') {
      setIsVisible(isVisible);
    }
  }, [isVisible]);

  return (
    <div className={className} ref={visibilityElementRef}>
      {children}
    </div>
  );
};

export const StickyArea: FC<StickyAreaProps> = ({
  product,
  visibilityOffset = -1,
  setHighlightUnselectedDimension,
}) => {
  const isMobile = useMedia({ desktop: false, mobile: true, tablet: false });
  const { isVisible } = useContext(StickyContext);
  const [isContentVisible, setIsContentVisible] = useState<boolean>(false);
  const { sku, availability } = product;

  useScrollPosition(
    ({ currPos }) => {
      if (typeof window !== 'undefined') {
        if (currPos.y * -1 < visibilityOffset) {
          setIsContentVisible(false);
        } else {
          setIsContentVisible(isVisible);
        }
      }
    },
    [isVisible],
  );

  return (
    <Transition in={isContentVisible} timeout={0}>
      {(animationState) => (
        <AreaWrapper
          position={isMobile ? 'bottom' : 'top'}
          hasAnimation={!isMobile}
          animationState={animationState}
          data-testid="sticky-area-wrapper"
        >
          <AreaContent isMobile={isMobile}>
            <ProductInformation
              isMobile={isMobile}
              product={product}
              animationState={animationState}
            />
            <StickyButtons
              sku={sku}
              isAvailable={
                availability &&
                (availability.state !== 'UNSELECTED' ? availability.isOrderable : true)
              }
              setHighlightUnselectedDimension={setHighlightUnselectedDimension}
              isSelected={availability && availability.state !== 'UNSELECTED'}
            />
          </AreaContent>
        </AreaWrapper>
      )}
    </Transition>
  );
};

export default StickyArea;
